import { Modal, Form, Input, Select, Row, Col, Divider, Switch, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clinicApi } from '../../../api/clinic.api';

const PASSWORD_RULES = [
  { required: true, message: 'Password is required' },
  { min: 8, message: 'At least 8 characters' },
  {
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
    message: 'Must include uppercase, lowercase, number & special character',
  },
];

const EDIT_PASSWORD_RULES = [
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8) return Promise.reject(new Error('At least 8 characters'));
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(value)) {
        return Promise.reject(new Error('Must include uppercase, lowercase, number & special character'));
      }
      return Promise.resolve();
    },
  },
];

const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);
const phoneRule = {
  pattern: /^0?[1-9][0-9]{9}$/,
  message: 'Enter a valid 10-digit number, or 11 digits with a single leading 0',
};

const DoctorForm = ({ open, onClose, onSubmit, initialValues, loading }) => {
  const [form] = Form.useForm();
  const isEdit = !!initialValues;

  const { data: clinicsData } = useQuery({
    queryKey: ['clinics-active'],
    queryFn: () => clinicApi.getActive(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue({
        name:           initialValues.name,
        phone:          initialValues.phone,
        specialization: initialValues.specialization,
        clinic:         initialValues.clinic?.id || initialValues.clinic,
        isActive:           initialValues.isActive !== false,
        isReferral:         initialValues.isReferral === true,
        deactivationReason: initialValues.deactivationReason || undefined,
        email:              initialValues.email,
      });
    }
  }, [open, initialValues, form]);

  return (
    <Modal
      title={isEdit ? 'Edit Doctor' : 'Add Doctor'}
      open={open} onCancel={onClose}
      onOk={() => form.validateFields().then(onSubmit)}
      confirmLoading={loading} width={580} destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Doctor Name"
              rules={[
                { required: true, message: 'Doctor name is required' },
                { min: 2, message: 'Name must be at least 2 characters' },
                { max: 100, message: 'Name cannot exceed 100 characters' },
              ]}
            >
              <Input placeholder="Dr. Name" maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="Mobile Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[{ required: true, message: 'Mobile number is required' }, phoneRule]}
            >
              <Input placeholder="Enter phone number" inputMode="numeric" pattern="[0-9]*" maxLength={11} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="specialization"
              label="Specialization"
              rules={[
                { required: true, message: 'Specialization is required' },
                { min: 2, message: 'Must be at least 2 characters' },
                { max: 100, message: 'Cannot exceed 100 characters' },
              ]}
            >
              <Input placeholder="e.g. Orthodontist" maxLength={100} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item shouldUpdate={(prev, cur) => prev.isReferral !== cur.isReferral} noStyle>
              {({ getFieldValue }) => {
                const isReferral = getFieldValue('isReferral');
                return (
                  <Form.Item
                    name="clinic"
                    label="Clinic"
                    rules={isReferral ? [] : [{ required: true, message: 'Please select a clinic' }]}
                  >
                    <Select
                      placeholder="Select clinic"
                      showSearch
                      optionFilterProp="children"
                      disabled={isReferral}
                      allowClear={isReferral}
                    >
                      {(clinicsData?.data || []).map((c) => (
                        <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>

        {/* Account Status toggle */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          background:     'var(--wl-primary-pale)',
          borderRadius:   10,
          padding:        '12px 16px',
          marginBottom:   16,
          border:         '1px solid var(--wl-primary-pale)',
        }}>
          <div>
            <Typography.Text strong style={{ color: 'var(--wl-primary)', fontSize: 13 }}>
              Account Status
            </Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
              Inactive doctors cannot log in to the system
            </Typography.Text>
          </div>
          <Form.Item name="isActive" valuePropName="checked" noStyle initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" style={{ minWidth: 88 }} />
          </Form.Item>
        </div>

        <Form.Item shouldUpdate={(prev, cur) => prev.isActive !== cur.isActive} noStyle>
          {({ getFieldValue }) => !getFieldValue('isActive') && (
            <Form.Item
              name="deactivationReason"
              label="Reason for Deactivation"
              rules={[
                { required: true, message: 'Please provide a reason for deactivating this doctor' },
                { min: 3, message: 'Reason must be at least 3 characters' },
                { max: 500, message: 'Reason cannot exceed 500 characters' },
              ]}
            >
              <Input.TextArea placeholder="e.g. On leave, left the clinic, etc." maxLength={500} rows={2} />
            </Form.Item>
          )}
        </Form.Item>

        {/* Referral Doctor toggle */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          background:     'var(--wl-primary-pale)',
          borderRadius:   10,
          padding:        '12px 16px',
          marginBottom:   16,
          border:         '1px solid var(--wl-primary-pale)',
        }}>
          <div>
            <Typography.Text strong style={{ color: 'var(--wl-primary)', fontSize: 13 }}>
              Referral Doctor
            </Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
              Mark this doctor as a referral doctor
            </Typography.Text>
          </div>
          <Form.Item name="isReferral" valuePropName="checked" noStyle initialValue={false}>
            <Switch
              checkedChildren="Yes"
              unCheckedChildren="No"
              style={{ minWidth: 88 }}
              onChange={(checked) => { if (checked) { form.setFieldValue('clinic', undefined); } }}
            />
          </Form.Item>
        </div>

        <Divider style={{ margin: '0 0 16px' }}>
          <LockOutlined /> Login Credentials {isEdit && '— optional update'}
        </Divider>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="email"
              label="Login Email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email address' },
                { pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Enter a strictly valid email format (e.g. user@domain.com)' },
              ]}
              extra="Used as username to login"
            >
              <Input placeholder="doctor@clinic.com" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={isEdit ? EDIT_PASSWORD_RULES : PASSWORD_RULES}
              extra={isEdit ? 'Leave blank to keep current password' : undefined}
            >
              <Input.Password placeholder={isEdit ? 'Enter new password to change' : 'Create a strong password'} autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const pwd = getFieldValue('password');
                    if (!pwd && isEdit) return Promise.resolve();
                    if (!value && pwd) return Promise.reject(new Error('Please confirm the password'));
                    if (value && value !== pwd) return Promise.reject(new Error('Passwords do not match'));
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Re-enter password" autoComplete="new-password" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default DoctorForm;
